import React, { useCallback, useEffect, useRef } from 'react';

/**
 * A TEXTAREA THAT GROWS WITH WHAT IS IN IT.
 *
 * Owner 2026-08-24: on the project setup, *"when 'the plot' is long, let the content push the textarea
 * height, so I don't have to scroll within and read through."* Right, and for a reason worth stating: a
 * scrollable box inside a scrollable page is two scroll contexts competing for the same wheel gesture. You
 * cannot see the whole plot at once, you lose your place when the inner box takes the scroll, and — worst for
 * a writing surface — you cannot judge the shape of what you wrote. Every editor this product is measured
 * against (Notion, Linear, GitHub's comment box) grows the field instead.
 *
 * IT STILL CAPS, though generously. Without a ceiling one very long field pushes every field under it off the
 * screen, and a form of six fields becomes a page you have to hunt through — trading an inner scrollbar for a
 * worse outer one. Past `maxPx` the box stops growing and scrolls, which is GitHub's behaviour and the point
 * at which "see it all at once" has stopped being achievable anyway.
 *
 * Re-fits when the value changes from OUTSIDE too (the AI assist replaces the whole value), which a
 * grow-on-keystroke implementation alone would miss.
 */
export interface AutoTextareaProps extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'value' | 'onChange' | 'rows'> {
  value: string;
  onChange: (value: string) => void;
  /** Height floor, in rows of text — what an empty field occupies. */
  minRows?: number;
  /** Ceiling in px, past which it scrolls rather than growing without end. */
  maxPx?: number;
}

const AutoTextarea: React.FC<AutoTextareaProps> = ({ value, onChange, minRows = 2, maxPx = 640, className = '', ...rest }) => {
  const ref = useRef<HTMLTextAreaElement>(null);

  const fit = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    // Collapse first: scrollHeight only reports the CONTENT height once the box is not already holding it.
    el.style.height = 'auto';
    const next = Math.min(el.scrollHeight, maxPx);
    el.style.height = `${next}px`;
    // Only show a scrollbar once we have actually hit the ceiling, so the common case has no inner scroll.
    el.style.overflowY = el.scrollHeight > maxPx ? 'auto' : 'hidden';
  }, [maxPx]);

  useEffect(fit, [fit, value]);

  return (
    <textarea
      {...rest}
      ref={ref}
      value={value}
      rows={minRows}
      onChange={(e) => { onChange(e.target.value); fit(); }}
      /* `resize-none`: the drag handle is meaningless on a box that sizes itself, and leaving it invites a
         creator to fight the component. */
      className={`resize-none ${className}`}
    />
  );
};

export default AutoTextarea;
